import type { MutableRefObject } from 'react';
import { useEffect, useRef } from 'react';

interface UseVideoLoadProps {
  videoRef: MutableRefObject<HTMLVideoElement | null>;
  src: string;
  onLoadComplete?: () => void;
  onLoadError?: () => void;
  retryCount?: number;
  delayTime?: number;
}

/**
 * 비디오 로딩 상태를 관리하는 훅
 *
 * @description
 * 비디오의 로딩 완료 여부(실제 재생 시작)를 추적하고 관리합니다.
 * currentTime > 0 이고 재생 중일 때 로딩 완료로 판단합니다.
 *
 * 에러 발생 시 retryCount만큼 delayTime 간격으로 비디오 로드를 재시도합니다.
 * 모든 재시도가 실패하면 onLoadError 콜백이 호출됩니다.
 *
 * @example
 * useVideoLoad({
 *   videoRef,
 *   src: videoSrc,
 *   onLoadComplete: () => void,
 *   onLoadError: () => void,
 *   retryCount: number, // 재시도 횟수 (기본값: 0)
 *   delayTime: number, // 재시도 딜레이 (기본값: 0ms)
 * });
 */
export const useVideoLoad = ({
  videoRef,
  src,
  onLoadComplete,
  onLoadError,
  retryCount = 0,
  delayTime = 0,
}: UseVideoLoadProps) => {
  const retryCountRef = useRef(0);
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    retryCountRef.current = 0;
    const video = videoRef.current;
    if (!video) return;

    // 비디오 로딩 실패 체크
    const handleVideoLoadError = () => {
      if (retryCountRef.current < retryCount) {
        // 비디오 로딩 재시도 횟수 증가
        retryCountRef.current += 1;

        // 비디오 로딩 재시도 딜레이 계산
        const delay = delayTime;

        timeoutIdRef.current = setTimeout(() => {
          // 비디오 로딩 재시도
          if (videoRef.current) {
            videoRef.current.load(); // 명시적 로드 호출
          }
        }, delay);

        return;
      }

      // 비디오 로딩 실패 (최대 재시도 횟수 도달)
      // console.error(`비디오 로딩 실패 (최대 재시도 횟수 도달): ${src}`);
      onLoadError?.();
      cleanup();
    };

    // 비디오 재생 시작 체크
    const handleVideoPlaying = () => {
      // currentTime > 0 이면 실제로 재생이 시작된 것으로 간주
      if (video.currentTime > 0 && !video.paused && !video.ended && video.readyState >= 2) {
        onLoadComplete?.();
        cleanup();
      }
    };

    // 이벤트 리스너 제거
    const cleanup = () => {
      video.removeEventListener('timeupdate', handleVideoPlaying);
      video.removeEventListener('playing', handleVideoPlaying);
      video.removeEventListener('error', handleVideoLoadError);

      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };

    // timeupdate 이벤트: 재생 위치가 변경될 때마다 발생
    video.addEventListener('timeupdate', handleVideoPlaying);
    // playing 이벤트: 재생이 시작되거나 일시 중지 후 다시 시작될 때 발생
    video.addEventListener('playing', handleVideoPlaying);
    video.addEventListener('error', handleVideoLoadError);

    // 이미 재생 중인 경우 체크
    if (video.currentTime > 0 && !video.paused && !video.ended && video.readyState >= 2) {
      handleVideoPlaying();
    }

    return cleanup;
  }, [src, onLoadComplete, onLoadError, retryCount, delayTime]);
};
